/**
 * syllabusQuery — 강의 v2 줄이 담는 **조건식(expr)**.
 *
 * 계약: `docs/learning-composition-v2/BE-20260911-reply-syllabus-preset.md` §2·§3·§6
 *       (정본 — 백엔드 `analysis/learning-composition/syllabus-v2-contract-2026-09-11.md`).
 *
 * 식 모양: { version: 1, groups: [ { terms: [ { field, match, values, source? } ] } ] }
 *   묶음(group) 사이 = 또는, 항(term) 사이 = 그리고, 값(values)은 match 로 엮는다(하나라도·모두·정확히·제외·is).
 *   개념 「제외」(none)는 값 가운데 하나도 붙지 않은 문제다 — 개념이 없는 문제도 통과한다(회신 BE-20260915 §1).
 *   화면에서 「제외」는 매스컨셉 방식이 아니라 「개념 범위」 조건의 한 갈래다(오너 2026-09-15) — isRangeTerm · pickableMatches.
 *
 * **검사기의 정본은 서버다**(회신 §3). 여기의 검사는 저장 전에 같은 판정을 먼저 보여 주는 미리 알림이라,
 * 백엔드 `services/syllabus_expr.py` 의 규칙·순서·한도를 한 줄씩 그대로 옮겼다(구조 E1~E5 → 모순 L1~L6).
 * 참조(R1 — 없는 개념·라벨·문제 번호)는 DB 가 있어야 판정되므로 서버만 한다.
 * 규칙이 바뀌면 이 파일과 검사(syllabusQuery.test.js — 백엔드 테스트 사례를 옮긴 것)를 함께 고친다.
 *
 * 경고 W1~W3·W5 는 서버가 만들지 않는 **화면 몫**이다(회신 §0 S2). 오류가 아니라 알림이다.
 * W4(지금 문제은행에 0건)는 식이 아니라 검색 결과로 아는 것이라 여기 두지 않는다 — 미리 세어 본 수가 0 이면 화면이 말한다.
 *
 * JS 와 파이썬의 차이 둘 — 알고 둔 것:
 *   - 3.0 과 3 을 JS 는 가를 수 없다(서버는 실수 3.0 을 거절). 화면은 정수만 만들므로 실제로 생기지 않는다.
 *   - 요청 크기(256KB)는 파이썬 json.dumps 의 기본 구분자(쉼표·쌍점 뒤 한 칸)로 센다 — serverJsonBytes.
 */

export const EXPR_VERSION = 1;

/** 한도(회신 §6 · 서버 LIMITS 그대로) */
export const LIMITS = {
  groups: 10,
  terms_per_group: 10,
  terms_total: 32,
  // 개념 항 하나 — 개념 표 크기(717)와 떼어 둔 여유값. 「순번 100 이상 제외」 한 항이 708개를 싣는다(회신 BE-20260915 §3 Q1)
  concept_values: 1000,
  // 식 전체의 하나라도·모두·정확히 값 합 — 「제외」 값은 세지 않는다(묶음마다 제외 항을 되풀이해도 막히지 않게, 같은 §3 Q2)
  concept_values_total: 2000,
  label_values: 50,
  label_values_total: 200,
  text_values: 5,
  text_values_total: 8,
  problem_values: 500,
  bytes: 262144,
};

/**
 * 축 열하나(회신 §2-1). kind 는 값의 모양, matches 는 허용 방식, single 은 한 문제가 값을 하나만 갖는 축(모순 L1 대상),
 * ref 는 참조 축(R1·R2), limit·total 은 값 개수 한도 키.
 */
export const FIELDS = {
  // 「제외」(none)는 목록 끝 — matches[0] 이 새 항의 기본 방식이다(quickExpr.defaultMatch · emptyTerm)
  concept: { label: '개념', kind: 'concept', matches: ['any', 'all', 'exact', 'none'], ref: 'concept', limit: 'concept_values', total: 'concept_values_total' },
  label: { label: '라벨', kind: 'label', matches: ['any', 'all'], ref: 'label', limit: 'label_values', total: 'label_values_total' },
  label_text: { label: '라벨 글자', kind: 'text', matches: ['any', 'all'], limit: 'text_values', total: 'text_values_total' },
  difficulty: { label: '난이도', kind: 'int', matches: ['any'], single: true, range: [1, 5] },
  author: { label: '작성자', kind: 'text', matches: ['any'], limit: 'text_values', total: 'text_values_total' },
  problem: { label: '문제 번호', kind: 'int', matches: ['any'], single: true, ref: 'problem', limit: 'problem_values' },
  origin: { label: '출처', kind: 'enum', matches: ['any'], single: true, allowed: [100, 200, 300, 900] },
  ai_kind: { label: 'AI 종류', kind: 'enum', matches: ['any'], single: true, allowed: ['generated', 'variation'] },
  has_answer: { label: '정답', kind: 'bool', matches: ['is'] },
  has_solution: { label: '풀이', kind: 'bool', matches: ['is'] },
  needs_analysis: { label: '분석 필요', kind: 'bool', matches: ['is'] },
};

/** 편집기의 축 고르기 순서 — 자주 쓰는 것 먼저(개념·라벨), 목록 박제(문제 번호)는 맨 끝(요청서 §3-2 「권하지 않음」) */
export const FIELD_ORDER = [
  'concept', 'label', 'label_text', 'difficulty', 'origin', 'ai_kind',
  'has_answer', 'has_solution', 'needs_analysis', 'author', 'problem',
];

export const MATCH_LABEL = { any: '하나라도', all: '모두', exact: '정확히', none: '제외', is: '' };

/** 화면에서 고르는 방식 — 「제외」(none)는 빼고. 제외는 「개념 범위」 조건에서만 고른다(오너 2026-09-15 「매스컨셉 조건에 같이 넣으면 안 됨」) */
export const pickableMatches = (field) => (FIELDS[field]?.matches || []).filter((m) => m !== 'none');

/** 화면만의 축 「개념 범위」 — 식에는 개념 항으로 들어간다. 이 이름은 편집기의 축 고르기와 경고 문장에만 쓴다 */
export const RANGE_FIELD = 'concept_range';
export const RANGE_LABEL = '개념 범위';

/**
 * 개념 범위 조건인가 — 개념 항 가운데 「제외」(none), 또는 순번 범위에서 펼친 「하나라도」(유래 source 가 있음).
 * 화면은 이 항을 매스컨셉 조건(칩 + 하나라도·모두·정확히)과 따로 다룬다(오너 2026-09-15 「범위 기능으로 별도로」).
 * 유래가 붙은 옛 「모두」·「정확히」 항은 매스컨셉 조건으로 둔다 — 범위 조건에는 그 방식이 없다.
 */
export const isRangeTerm = (t) => t?.field === 'concept' && (t.match === 'none' || (t.match === 'any' && t.source?.kind === 'range'));

const SINGLE_FIELDS = ['difficulty', 'problem', 'origin', 'ai_kind'];
const BOOL_FIELDS = ['has_answer', 'has_solution', 'needs_analysis'];
export const TEXT_MAX_LEN = 100;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * 출처 고르기 — 화면은 셋으로 묶고 계약은 넷을 따로 둔다(회신 §1-1).
 * 「사람」은 100(출처 기록 뒤 사람이 만든 것)과 900(출처 기록 전 기존 문제 — 확정 문제의 98.8%)을 함께 보낸다.
 * 백엔드 스스로 900 을 「배포 이전 레거시 사람 문제」로 정의했고(2026-08-26 회신 F1), 100 만 보내면 지금은 0건이다.
 */
export const ORIGIN_CHOICES = [
  { key: 'human', label: '사람', values: [100, 900], hint: '출처 기록 전 기존 문제 포함' },
  { key: 'ai', label: 'AI', values: [200] },
  { key: 'ocr', label: 'OCR 등록', values: [300] },
];
const ORIGIN_NAME = { 100: '사람(신규)', 200: 'AI', 300: 'OCR 등록', 900: '기존 문제' };
export const AI_KIND_NAME = { generated: 'AI 생성', variation: 'AI 변형' };

/** 식 검사 실패 — reason 은 서버와 같은 기계 사유, data 는 path·detail·limit·max·group·terms·rule */
export class ExprCheckError extends Error {
  constructor(message, reason, data = {}) {
    super(message);
    this.name = 'ExprCheckError';
    this.reason = reason;
    this.data = data;
  }
}

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isInt = (v) => typeof v === 'number' && Number.isInteger(v);
const pathOf = (i, j = null) => (j == null ? `groups[${i}]` : `groups[${i}].terms[${j}]`);
const invalid = (message, path, detail) => new ExprCheckError(message, 'invalid_expr', { path, detail });
const tooLarge = (limit, max, path = null) => new ExprCheckError(
  `식이 한도를 넘었습니다(${limit} ≤ ${max})`, 'expr_too_large', path == null ? { limit, max } : { limit, max, path },
);
const dedup = (values) => values.filter((v, k) => values.indexOf(v) === k);
const codePoints = (s) => Array.from(s).length;

/** 오류 위치 'groups[i].terms[j]' → { group, term } (없는 자리는 null) */
export function parsePath(path) {
  const m = /^groups\[(\d+)\](?:\.terms\[(\d+)\])?$/.exec(String(path || ''));
  if (!m) return { group: null, term: null };
  return { group: Number(m[1]), term: m[2] != null ? Number(m[2]) : null };
}

function utf8Length(s) {
  let n = 0;
  for (const ch of s) {
    const c = ch.codePointAt(0);
    n += c < 0x80 ? 1 : (c < 0x800 ? 2 : (c < 0x10000 ? 3 : 4));
  }
  return n;
}

/**
 * 서버가 세는 요청 크기 — 파이썬 json.dumps(ensure_ascii=False) 의 바이트 수. 기본 구분자가 「, 」·「: 」 라
 * JSON.stringify 보다 쉼표·쌍점마다 한 바이트씩 크다. 한도 가까이에서 두 쪽 판정이 갈리지 않게 같은 자로 잰다.
 */
export function serverJsonBytes(v) {
  if (v === null || v === undefined) return 4;
  if (typeof v === 'boolean') return v ? 4 : 5;
  if (typeof v === 'number') return String(v).length;
  if (typeof v === 'string') return utf8Length(JSON.stringify(v));
  if (Array.isArray(v)) {
    if (!v.length) return 2;
    return 2 + v.reduce((a, x) => a + serverJsonBytes(x), 0) + 2 * (v.length - 1);
  }
  if (typeof v === 'object') {
    const keys = Object.keys(v).filter((k) => v[k] !== undefined);
    if (!keys.length) return 2;
    return 2 + keys.reduce((a, k) => a + utf8Length(JSON.stringify(k)) + 2 + serverJsonBytes(v[k]), 0) + 2 * (keys.length - 1);
  }
  return 4;
}

function normValue(spec, v, path) {
  const bad = invalid(`${spec.label} 값의 형식이 잘못되었습니다: ${JSON.stringify(v)}`, path, 'bad_value');
  const { kind } = spec;
  if (kind === 'concept' || kind === 'int') {
    if (!isInt(v)) throw bad;
    if (spec.range && !(v >= spec.range[0] && v <= spec.range[1])) {
      throw invalid(`${spec.label} 값은 ${spec.range[0]}~${spec.range[1]} 사이 정수여야 합니다: ${v}`, path, 'bad_value');
    }
    return v;
  }
  if (kind === 'label') {
    if (typeof v !== 'string') throw bad;
    const s = v.trim().toLowerCase();
    if (!UUID_RE.test(s)) throw invalid(`${spec.label} 값은 uuid 형식이어야 합니다: ${v}`, path, 'bad_value');
    return s;
  }
  if (kind === 'text') {
    if (typeof v !== 'string') throw bad;
    const s = v.trim();
    if (!s) throw invalid(`${spec.label} 값이 비어 있습니다`, path, 'empty');
    if (codePoints(s) > TEXT_MAX_LEN) throw invalid(`${spec.label} 값은 ${TEXT_MAX_LEN}자 이하여야 합니다`, path, 'bad_value');
    return s;
  }
  if (kind === 'enum') {
    if (typeof v === 'boolean' || !(typeof v === 'number' || typeof v === 'string') || !spec.allowed.includes(v)) {
      throw invalid(`${spec.label} 값이 허용 목록 밖입니다: ${JSON.stringify(v)}`, path, 'bad_value');
    }
    return v;
  }
  if (kind === 'bool') {
    if (typeof v !== 'boolean') throw invalid(`${spec.label} 값은 참·거짓이어야 합니다: ${JSON.stringify(v)}`, path, 'bad_value');
    return v;
  }
  throw bad;
}

function normSource(field, src, path) {
  const bad = invalid('순번 범위 형식이 잘못되었습니다(개념 항에만 · 시작 ≤ 끝 · 정수)', path, 'bad_source');
  if (field !== 'concept' || !isObj(src) || src.kind !== 'range') throw bad;
  const a = src.order_from;
  const b = src.order_to;
  if (!isInt(a) || !isInt(b) || a > b) throw bad;
  return { kind: 'range', order_from: a, order_to: b };
}

function normTerm(i, j, t) {
  const path = pathOf(i, j);
  if (!isObj(t)) throw invalid('항은 객체여야 합니다', path, 'bad_value');
  const { field } = t;
  const spec = typeof field === 'string' && Object.prototype.hasOwnProperty.call(FIELDS, field) ? FIELDS[field] : null;
  if (!spec) throw invalid(`알 수 없는 검색 축입니다: ${JSON.stringify(field ?? null)}`, path, 'unknown_field');
  const { values } = t;
  if (!Array.isArray(values) || !values.length) throw invalid(`${spec.label} 값이 비어 있습니다`, path, 'empty');
  if (!spec.matches.includes(t.match)) {
    throw invalid(`${spec.label}에는 「${MATCH_LABEL[t.match] || t.match || '방식 없음'}」을 쓸 수 없습니다`, path, 'match_not_allowed');
  }
  const vals = dedup(values.map((v) => normValue(spec, v, path)));
  if (spec.kind === 'bool' && vals.length !== 1) throw invalid(`${spec.label} 값은 정확히 하나여야 합니다`, path, 'bad_value');
  if (spec.limit && vals.length > LIMITS[spec.limit]) throw tooLarge('values', LIMITS[spec.limit], path);
  const out = { field, match: t.match, values: vals };
  if (t.source !== undefined && t.source !== null) out.source = normSource(field, t.source, path);
  return out;
}

/**
 * 구조·한도 검사 + 정규화(서버 normalize 와 같은 순서) — 정규화된 식을 돌려주고, 어기면 ExprCheckError.
 * 정규화: 값 중복 제거 · 라벨 uuid 소문자 · 글자 앞뒤 공백 제거 · 항의 낯선 키 버림.
 */
export function normalizeExpr(expr) {
  if (!isObj(expr)) throw invalid('식은 객체여야 합니다', '', 'bad_value');
  if (serverJsonBytes(expr) > LIMITS.bytes) throw tooLarge('bytes', LIMITS.bytes);
  if (!(isInt(expr.version) && expr.version === EXPR_VERSION)) {
    throw new ExprCheckError('지원하지 않는 식 버전입니다(version=1 만)', 'unsupported_version');
  }
  const { groups } = expr;
  if (!Array.isArray(groups)) throw invalid('groups 는 배열이어야 합니다', 'groups', 'bad_value');
  if (!groups.length) throw invalid('식에 묶음이 없습니다', 'groups', 'empty');
  if (groups.length > LIMITS.groups) throw tooLarge('groups', LIMITS.groups);

  const outGroups = [];
  const totals = {};
  let nTerms = 0;
  groups.forEach((g, i) => {
    if (!isObj(g)) throw invalid('묶음은 객체여야 합니다', pathOf(i), 'bad_value');
    const { terms } = g;
    if (!Array.isArray(terms) || !terms.length) throw invalid(`묶음 ${i + 1}에 조건이 없습니다`, pathOf(i), 'empty');
    if (terms.length > LIMITS.terms_per_group) throw tooLarge('terms_per_group', LIMITS.terms_per_group, pathOf(i));
    nTerms += terms.length;
    if (nTerms > LIMITS.terms_total) throw tooLarge('terms_total', LIMITS.terms_total);
    const outTerms = terms.map((t, j) => {
      const nt = normTerm(i, j, t);
      const key = FIELDS[nt.field].total;
      // 개념 「제외」 값은 합산하지 않는다 — 상한은 요청 크기(256KB)가 맡는다(서버 normalize 와 같다)
      if (key && !(nt.field === 'concept' && nt.match === 'none')) {
        totals[key] = (totals[key] || 0) + nt.values.length;
        if (totals[key] > LIMITS[key]) throw tooLarge(key, LIMITS[key]);
      }
      return nt;
    });
    outGroups.push({ terms: outTerms });
  });
  return { version: EXPR_VERSION, groups: outGroups };
}

const setEq = (a, b) => a.size === b.size && [...a].every((v) => b.has(v));
const subset = (a, b) => [...a].every((v) => b.has(v));

function contradiction(i, terms, rule, message) {
  return new ExprCheckError(`묶음 ${i + 1} — ${message}`, 'contradiction', {
    group: i, terms: [...new Set(terms)].sort((a, b) => a - b), rule,
  });
}

function checkGroup(i, terms) {
  const by = {};
  terms.forEach((t, j) => { (by[t.field] = by[t.field] || []).push([j, t]); });
  // L1 한 값 축의 항 둘 이상 — 교집합이 비면 항상 0건
  SINGLE_FIELDS.forEach((f) => {
    const ts = by[f] || [];
    if (ts.length > 1) {
      let acc = new Set(ts[0][1].values);
      ts.slice(1).forEach(([, t]) => { acc = new Set(t.values.filter((v) => acc.has(v))); });
      if (!acc.size) {
        throw contradiction(i, ts.map(([j]) => j), 'single_value_disjoint', `${FIELDS[f].label} 조건들의 값이 겹치지 않아 항상 0건입니다`);
      }
    }
  });
  // L2 같은 참·거짓 축의 있음과 없음
  BOOL_FIELDS.forEach((f) => {
    const ts = by[f] || [];
    const vals = new Set(ts.map(([, t]) => t.values[0]));
    if (vals.has(true) && vals.has(false)) {
      throw contradiction(i, ts.map(([j]) => j), 'bool_conflict', `${FIELDS[f].label}의 있음과 없음이 함께 있습니다`);
    }
  });
  // L3 분석 필요(참) + 개념 + 난이도 — 거짓일 때는 모순 아님. 개념은 요구하는 항만 센다(「제외」는 개념을 요구하지 않는다)
  const naTrue = (by.needs_analysis || []).filter(([, t]) => t.values[0] === true).map(([j]) => j);
  const conceptReq = (by.concept || []).filter(([, t]) => t.match !== 'none');
  if (naTrue.length && conceptReq.length && by.difficulty) {
    throw contradiction(i, [...naTrue, ...conceptReq.map(([j]) => j), ...by.difficulty.map(([j]) => j)],
      'needs_analysis_conflict', '분석 필요(개념 또는 난이도 없음)인데 개념과 난이도를 둘 다 요구합니다');
  }
  // L4 AI 종류 + AI(200) 없는 출처
  if (by.ai_kind) {
    const bad = (by.origin || []).filter(([, t]) => !t.values.includes(200)).map(([j]) => j);
    if (bad.length) {
      throw contradiction(i, [...by.ai_kind.map(([j]) => j), ...bad], 'ai_kind_origin', 'AI 종류는 AI 문제에만 있는데 출처에 AI 가 없습니다');
    }
  }
  // L5 개념 「정확히」와 맞지 않는 다른 개념 항 — 「제외」는 반대로 겹치면 모순(정확히 가져야 할 개념을 빼라는 뜻)
  const concepts = by.concept || [];
  const exacts = concepts.filter(([, t]) => t.match === 'exact');
  if (exacts.length) {
    const [j0, t0] = exacts[0];
    const ex = new Set(t0.values);
    concepts.forEach(([j, t]) => {
      const s = new Set(t.values);
      const meets = [...s].some((v) => ex.has(v));
      let ok = meets;
      if (t.match === 'exact') ok = setEq(s, ex);
      else if (t.match === 'all') ok = subset(s, ex);
      else if (t.match === 'none') ok = !meets;
      if (!ok) throw contradiction(i, [j0, j], 'exact_conflict', '개념이 「정확히」 정해졌는데 다른 개념 조건이 그 집합과 맞지 않습니다');
    });
  }
  // L6 요구한 개념을 같은 묶음에서 제외 — 「하나라도」 값이 전부 제외 목록 안 / 「모두」 값 하나라도 제외 목록 안이면 항상 0건
  const excluded = new Set(concepts.filter(([, t]) => t.match === 'none').flatMap(([, t]) => t.values));
  if (excluded.size) {
    const noneJs = concepts.filter(([, t]) => t.match === 'none').map(([j]) => j);
    concepts.forEach(([j, t]) => {
      const s = new Set(t.values);
      if ((t.match === 'any' && subset(s, excluded)) || (t.match === 'all' && [...s].some((v) => excluded.has(v)))) {
        throw contradiction(i, [...noneJs, j], 'exclude_conflict', '요구한 개념이 같은 묶음의 제외 범위에 들어 있어 항상 0건입니다');
      }
    });
  }
}

/** 모순 L1~L6 — 정규화된 식을 받는다. 묶음 하나 안에서만 본다(묶음 사이는 「또는」이라 모순이 없다) */
export function checkContradictions(expr) {
  expr.groups.forEach((g, i) => checkGroup(i, g.terms));
}

/** 저장(N1·N2) 전 검사 — 구조·한도 → 모순. 참조(R1)는 서버만. 정규화된 식을 돌려준다 */
export function validateForStore(expr) {
  const n = normalizeExpr(expr);
  checkContradictions(n);
  return n;
}

/** 검색(POST) 전 검사 — 구조·한도만(모순은 서버가 0건으로 계산, 회신 §3) */
export function validateForSearch(expr) {
  return normalizeExpr(expr);
}

const termKey = (t) => JSON.stringify([t.field, t.match, [...(t.values || [])].map(String).sort()]);

/**
 * 화면 몫의 경고 W1~W3·W5(서버는 만들지 않는다 — 오류가 아니다). 편집 중 원본을 받는다(값 중복을 보려고).
 * W5 — 개념 조건이 「제외」뿐인 묶음은 개념이 없는 문제(분석 전)도 통과한다. 서버는 막지 않고 알려 두라고만 했다(회신 BE-20260915 §1).
 * 반환 [{ code, group, term?, message }] — 전부 돌려준다(첫 것에서 멈추지 않는다).
 */
export function exprWarnings(expr) {
  const out = [];
  const groups = Array.isArray(expr?.groups) ? expr.groups : [];
  const keysOf = groups.map((g) => (Array.isArray(g?.terms) ? g.terms.filter(isObj).map(termKey) : []));
  groups.forEach((g, i) => {
    const terms = Array.isArray(g?.terms) ? g.terms : [];
    terms.forEach((t, j) => {
      const vals = Array.isArray(t?.values) ? t.values : [];
      if (dedup(vals).length !== vals.length) {
        out.push({ code: 'W1', group: i, term: j, message: `묶음 ${i + 1}의 조건 ${j + 1} — 같은 값이 되풀이됐습니다. 한 번만 셉니다` });
      }
      if (t?.field === 'concept' && t.match === 'all' && dedup(vals).length > 5) {
        out.push({ code: 'W3', group: i, term: j, message: `개념 ${dedup(vals).length}개를 모두 가진 문제는 거의 없습니다 — 「하나라도」가 맞는지 확인하세요` });
      }
    });
    const ks = keysOf[i];
    if (new Set(ks).size !== ks.length) {
      out.push({ code: 'W1', group: i, message: `묶음 ${i + 1}에 같은 조건이 되풀이됐습니다` });
    }
    const concepts = terms.filter((t) => t?.field === 'concept');
    if (concepts.length && concepts.every((t) => t.match === 'none')) {
      out.push({ code: 'W5', group: i, message: `묶음 ${i + 1} — 개념 범위 제외만 있어 개념이 없는 문제(분석 전)도 통과합니다` });
    }
  });
  for (let i = 0; i < keysOf.length; i += 1) {
    for (let j = 0; j < keysOf.length; j += 1) {
      if (i !== j && keysOf[i].length) {
        const a = new Set(keysOf[i]);
        const b = new Set(keysOf[j]);
        if (setEq(a, b) && i < j) {
          out.push({ code: 'W1', group: j, message: `묶음 ${j + 1}은 묶음 ${i + 1}과 같습니다` });
        } else if (a.size < b.size && subset(a, b)) {
          out.push({ code: 'W2', group: j, message: `묶음 ${j + 1}은 묶음 ${i + 1}에 흡수됩니다 — 결과는 묶음 ${i + 1}만 둔 것과 같습니다` });
        }
      }
    }
  }
  return out;
}

/**
 * 한 번에 판정 — 편집기가 매 입력마다 부른다. { ok, normalized, error, warnings }.
 * error 는 { reason, message, group, term, terms, detail, limit, max, rule } 로 풀어 화면이 그 자리에 빨간 표시를 붙인다.
 */
export function inspectExpr(expr, { mode = 'store' } = {}) {
  const warnings = exprWarnings(expr);
  try {
    const normalized = mode === 'search' ? validateForSearch(expr) : validateForStore(expr);
    return { ok: true, normalized, error: null, warnings };
  } catch (e) {
    if (!(e instanceof ExprCheckError)) throw e;
    const { group, term } = parsePath(e.data.path);
    return {
      ok: false,
      normalized: null,
      error: {
        reason: e.reason,
        message: e.message,
        group: e.data.group ?? group,
        term,
        terms: e.data.terms || (term != null ? [term] : []),
        detail: e.data.detail ?? null,
        limit: e.data.limit ?? null,
        max: e.data.max ?? null,
        rule: e.data.rule ?? null,
      },
      warnings,
    };
  }
}

// ── 편집 재료 ──

/** 새 조건 한 줄 — 방식은 그 축의 첫 허용값, 참·거짓 축은 「있음(참)」으로 시작 */
export function emptyTerm(field = 'concept') {
  const spec = FIELDS[field] || FIELDS.concept;
  return { field, match: spec.matches[0], values: spec.kind === 'bool' ? [true] : [] };
}

export const emptyGroup = () => ({ terms: [emptyTerm('concept')] });
export const emptyExpr = () => ({ version: EXPR_VERSION, groups: [emptyGroup()] });

/**
 * 축을 바꾸면 값은 비운다 — 개념 번호가 난이도 칸에 남으면 형식 오류가 되고, 사람이 그 이유를 모른다.
 * 방식은 새 축이 허용하는 것 중 지금 방식이 있으면 유지한다.
 */
export function changeField(term, field) {
  const next = emptyTerm(field);
  // 「제외」는 넘기지 않는다 — 개념 범위 조건의 방식이라 다른 축(또는 매스컨셉 조건)으로 따라가면 뜻이 뒤집힌다
  if (FIELDS[field]?.matches.includes(term?.match) && term.match !== 'none') next.match = term.match;
  return next;
}

/**
 * 순번 범위 → 개념 번호(요청서 §3-2). 사전의 order(교육과정 전역 순번)가 [from, to] 인 개념을 순번 순으로.
 * 폐기(is_outdated) 개념은 뺀다 — 새로 고르는 조건에 대체된 개념을 넣지 않는다(서버는 폐기 개념도 받는다).
 * withOutdated — 폐기 개념도 싣는다. 「제외」 항이 쓴다 — 폐기 개념이 붙은 옛 문제도 빼야 해서다(요청서 FE-20260914 Q1).
 */
export function expandOrderRange(dict, from, to, { withOutdated = false } = {}) {
  const a = Number(from);
  const b = Number(to);
  if (!Number.isInteger(a) || !Number.isInteger(b) || a > b) return [];
  return (Array.isArray(dict) ? dict : [])
    .filter((c) => c && Number.isInteger(c.order) && c.order >= a && c.order <= b && (withOutdated || !c.is_outdated))
    .sort((x, y) => x.order - y.order)
    .map((c) => c.id ?? c.concept_id);
}

/** 그 항의 방식대로 펼친다 — 「제외」면 폐기 개념까지. 채우기·유래 대조·어긋남 알림이 모두 이것으로 같은 집합을 본다 */
export const expandOrderRangeFor = (dict, from, to, match) => expandOrderRange(dict, from, to, { withOutdated: match === 'none' });

/** 사전 순번의 처음·끝 { min, max } — 순번 있는 개념이 없으면 null */
export function orderBounds(dict) {
  let min = null;
  let max = null;
  (Array.isArray(dict) ? dict : []).forEach((c) => {
    if (!c || !Number.isInteger(c.order)) return;
    if (min == null || c.order < min) min = c.order;
    if (max == null || c.order > max) max = c.order;
  });
  return min == null ? null : { min, max };
}

/**
 * 순번 입력 두 칸 → { from, to }. 한쪽이 비면 사전의 처음·끝 순번으로 채운다 — 「100 이상」은 100~끝, 「50 이하」는 처음~50.
 * 서버의 유래 형식이 정수 둘이라 빈 끝을 보낼 수 없어서다(요청서 FE-20260914 §1-6).
 * 둘 다 비었거나, 정수가 아니거나, 시작이 끝보다 크면 null.
 */
export function resolveOrderRange(dict, from, to) {
  const f = String(from ?? '').trim();
  const t = String(to ?? '').trim();
  if (!f && !t) return null;
  const bounds = orderBounds(dict);
  const a = f ? Number(f) : bounds?.min;
  const b = t ? Number(t) : bounds?.max;
  if (!Number.isInteger(a) || !Number.isInteger(b) || a > b) return null;
  return { from: a, to: b };
}

/**
 * 범위 유래의 모양 — 끝이 사전의 끝이면 'atLeast'(이상), 시작이 사전의 처음이면 'atMost'(이하), 그 밖(둘 다·전체)은 'between'.
 * bounds(orderBounds)가 없으면 늘 'between'.
 */
export function orderRangeShape(src, bounds = null) {
  if (!src || !bounds) return 'between';
  const toEnd = src.order_to >= bounds.max;
  const fromStart = src.order_from <= bounds.min;
  if (toEnd && !fromStart) return 'atLeast';
  if (fromStart && !toEnd) return 'atMost';
  return 'between';
}

/** 범위 유래를 사람 말로 — 「순번 100 이상」·「순번 50 이하」·「순번 100~200」(모양은 orderRangeShape) */
export function orderRangeText(src, bounds = null) {
  if (!src) return '';
  const shape = orderRangeShape(src, bounds);
  if (shape === 'atLeast') return `순번 ${src.order_from} 이상`;
  if (shape === 'atMost') return `순번 ${src.order_to} 이하`;
  return `순번 ${src.order_from}~${src.order_to}`;
}

/** 출처 고르기 — 이 선택지의 값이 모두 들어 있으면 켜진 것 */
export const originChoiceOn = (values, choice) => choice.values.every((v) => (values || []).includes(v));

/** 출처 고르기를 켜고 끈다 — 끌 때는 그 선택지의 값만 뺀다(다른 선택지와 겹치는 값이 없다) */
export function toggleOriginChoice(values, choice) {
  const cur = Array.isArray(values) ? values : [];
  if (originChoiceOn(cur, choice)) return cur.filter((v) => !choice.values.includes(v));
  return dedup([...cur, ...choice.values]);
}

// ── 사람이 읽는 해석 ──

const clip = (names, max = 6) => (names.length > max ? `${names.slice(0, max).join(' · ')} 외 ${names.length - max}개` : names.join(' · '));

function originNames(values) {
  const v = [...values];
  const names = [];
  if (v.includes(100) && v.includes(900)) {
    names.push('사람');
    v.splice(v.indexOf(100), 1);
    v.splice(v.indexOf(900), 1);
  }
  return names.concat(v.map((x) => ORIGIN_NAME[x] || String(x)));
}

/**
 * 조건 한 줄을 문장으로 — 「개념 A · B 중 하나」「순번 100 이상 제외」「난이도 3 · 4」「정답 있음」.
 * 개념 범위 조건(isRangeTerm)은 개념 이름 대신 범위로 적는다 — 「순번 100~200 포함」·「순번 100 이상 제외」.
 * names: { concept(id) → 이름|null, label(uuid) → 이름|null, orderBounds?() → { min, max }|null } — 모르면 번호로 적는다.
 */
export function describeTerm(term, names = {}) {
  const spec = FIELDS[term?.field];
  if (!spec) return '알 수 없는 조건';
  const vals = Array.isArray(term.values) ? term.values : [];
  if (spec.kind === 'bool') {
    const on = vals[0] === true;
    if (term.field === 'needs_analysis') return on ? '분석 필요(개념 또는 난이도 없음)' : '분석 완비(개념·난이도 모두 있음)';
    return `${spec.label} ${on ? '있음' : '없음'}`;
  }
  if (!vals.length) return `${spec.label} (값 없음)`;
  let shown;
  if (term.field === 'concept') shown = vals.map((v) => (names.concept && names.concept(v)) || `#${v}`);
  else if (term.field === 'label') shown = vals.map((v) => `「${(names.label && names.label(v)) || '라벨'}」`);
  else if (term.field === 'origin') shown = originNames(vals);
  else if (term.field === 'ai_kind') shown = vals.map((v) => AI_KIND_NAME[v] || v);
  else if (spec.kind === 'text') shown = vals.map((v) => `「${v}」`);
  else shown = vals.map(String);
  const bounds = typeof names.orderBounds === 'function' ? names.orderBounds() : null;
  const src = term.source ? ` (${orderRangeText(term.source, bounds)}에서 펼침)` : '';
  if (spec.kind === 'text') {
    const verb = term.match === 'all' ? '모두 포함' : (vals.length > 1 ? '중 하나 포함' : '포함');
    return `${spec.label}에 ${clip(shown)} ${verb}`;
  }
  if (isRangeTerm(term) && term.source) return `${orderRangeText(term.source, bounds)} ${term.match === 'none' ? '제외' : '포함'}`;
  if (term.match === 'none') return `${spec.label} ${clip(shown)} 제외`; // 범위 기록 없는 제외(다른 경로로 만든 식)
  if (term.match === 'all') return `${spec.label} ${clip(shown)} 모두${src}`;
  if (term.match === 'exact') return `${spec.label}이 정확히 ${clip(shown)}${src}`;
  return `${spec.label} ${clip(shown)}${shown.length > 1 ? ' 중 하나' : ''}${src}`;
}

/** 식 전체를 문장으로 — 「(A 그리고 B) 또는 (C)」. 묶음이 하나면 괄호를 치지 않는다 */
export function describeExpr(expr, names = {}) {
  const groups = Array.isArray(expr?.groups) ? expr.groups : [];
  if (!groups.length) return '조건 없음';
  const parts = groups.map((g) => (Array.isArray(g?.terms) ? g.terms : []).map((t) => describeTerm(t, names)).join(' 그리고 '));
  return parts.length === 1 ? parts[0] : parts.map((p) => `(${p})`).join(' 또는 ');
}
